import { PdfHelpers, createPdfHelpers, uploadAndRegisterPdf, PDF_MARGIN_MM } from './gestion-tecnica-documentos'
import { RoomInput, ReflectanceInput, SpacingResult, notaSobreRangoArt10 } from './iluminacion'
import { LightingCalculationRow, LuminaireCatalogItem, AVISO_AREA_CLASIFICADA } from './gestion-tecnica-iluminacion'
import { calculatePlantScale, calculateLuminairePositionsPx } from '../components/LuminairePlantView'
import {
  estimateDirectUniformity, presentarUniformidadDirecta, UNIFORMIDAD_DIRECTA_NOTA, ORIENTACION_FOTOMETRIA_NOTA,
} from './ies-photometric'

// Forma real de calculo.inputs (guardado como jsonb libre en lighting_calculations,
// ver handleGuardar() en IluminacionMethodologyPage.tsx).
interface LightingCalculationInputs {
  room: RoomInput
  reflectances: ReflectanceInput
  luminaire?: LuminaireCatalogItem
  coeficienteUtilizacionManual?: number
  ambiente: 'limpio' | 'sucio'
  customPositions?: Record<number, { xMeters: number; yMeters: number }>
}

// Dimensiones (en mm, mismas unidades que createPdfHelpers) de la caja donde
// se dibuja la planta dentro del PDF. calculatePlantScale()/calculateLuminairePositionsPx()
// son agnósticas de unidad (solo hacen aritmética de escalado 2D), así que se
// reutilizan tal cual con "mm" en vez de "px" — sin reescribir la fórmula.
const PLANT_BOX_WIDTH_MM = 160
const PLANT_BOX_HEIGHT_MM = 95
const PLANT_BOX_MARGIN_MM = 8

// Dibuja el contorno del local y las luminarias posicionadas directamente
// sobre el jsPDF con doc.rect()/doc.circle()/doc.text(), portando la misma
// lógica matemática que LuminairePlantView.tsx usa para el SVG interactivo.
function drawPlantOnPdf(
  helpers: PdfHelpers,
  room: RoomInput,
  espaciado: SpacingResult,
  customPositions?: Record<number, { xMeters: number; yMeters: number }>
): void {
  const { doc } = helpers

  // El espacio (título + caja) ya fue reservado por el llamador antes de
  // imprimir el título de la sección, para que ambos salten de página juntos
  // si no caben — no se vuelve a llamar checkBreak() aquí.

  const pageX = PDF_MARGIN_MM
  const pageY = helpers.getY()

  const plantScale = calculatePlantScale(room, PLANT_BOX_WIDTH_MM, PLANT_BOX_HEIGHT_MM, PLANT_BOX_MARGIN_MM)
  const positions = calculateLuminairePositionsPx(espaciado, plantScale)

  // Contorno del local
  doc.setDrawColor(120, 120, 120)
  doc.setFillColor(245, 245, 245)
  doc.rect(
    pageX + plantScale.offsetX,
    pageY + plantScale.offsetY,
    plantScale.roomWidthPx,
    plantScale.roomHeightPx,
    'FD'
  )

  // Cotas de dimensión
  doc.setFontSize(8)
  doc.setTextColor(90, 90, 90)
  doc.text(
    `${room.ancho.toFixed(2)} m`,
    pageX + plantScale.offsetX + plantScale.roomWidthPx / 2,
    pageY + plantScale.offsetY - 2,
    { align: 'center' }
  )
  doc.text(
    `${room.largo.toFixed(2)} m`,
    pageX + plantScale.offsetX - 3,
    pageY + plantScale.offsetY + plantScale.roomHeightPx / 2,
    { align: 'center', angle: 90 }
  )

  // Luminarias — mismo criterio de color que LuminairePlantView (amarillo =
  // posición de grilla, azul = reposicionada manualmente).
  positions.forEach(pos => {
    const custom = customPositions?.[pos.index]
    const cx = custom ? plantScale.offsetX + custom.xMeters * plantScale.scale : pos.x
    const cy = custom ? plantScale.offsetY + custom.yMeters * plantScale.scale : pos.y
    doc.setFillColor(custom ? 59 : 251, custom ? 130 : 191, custom ? 246 : 36)
    doc.circle(pageX + cx, pageY + cy, 1.6, 'F')
  })

  helpers.skip(PLANT_BOX_HEIGHT_MM + 6)
}

// Genera el reporte técnico PDF de un cálculo de Iluminación (Art. 10 Anexo 3
// AM 196), siguiendo el mismo patrón que generateJobDescriptionPDF() (GT-02):
// createPdfHelpers() para el flujo de texto + uploadAndRegisterPdf() para
// subir el blob y registrarlo en sso_documents.
export async function generateLightingReportPDF(
  calculo: LightingCalculationRow,
  companyId: string,
  companyName: string,
  uploadedBy: string | null
): Promise<string | null> {
  const helpers = createPdfHelpers()
  const { doc, txt, skip, hline } = helpers
  const inputs = calculo.inputs as LightingCalculationInputs
  const resultado = calculo.resultado

  // a. Cabecera
  txt(companyName, 14, 'bold', 'center', 0, 2)
  txt('Reporte de Cálculo de Iluminación (GT-15)', 12, 'bold', 'center', 0, 6)
  txt(calculo.nombre_local, 11, 'bold', 'center', 0, 6)
  hline()
  skip(4)

  // b. Resumen del local
  txt('Resumen del local', 11, 'bold', 'left', 0, 4)
  txt(`Largo: ${inputs.room.largo} m`, 9, 'normal', 'left', 0, 2)
  txt(`Ancho: ${inputs.room.ancho} m`, 9, 'normal', 'left', 0, 2)
  txt(`Altura: ${inputs.room.altura} m`, 9, 'normal', 'left', 0, 2)
  txt(`Altura plano de trabajo: ${inputs.room.alturaPlanoTrabajo} m`, 9, 'normal', 'left', 0, 2)
  txt(`Reflectancia techo: ${inputs.reflectances.techo}%`, 9, 'normal', 'left', 0, 2)
  txt(`Reflectancia paredes: ${inputs.reflectances.paredes}%`, 9, 'normal', 'left', 0, 2)
  txt(`Reflectancia piso: ${inputs.reflectances.piso}%`, 9, 'normal', 'left', 0, 4)
  hline()
  skip(4)

  // c. Resultado del cálculo
  txt('Resultado del cálculo', 11, 'bold', 'left', 0, 4)
  txt(`Iluminancia resultante: ${resultado.iluminanciaResultante.toFixed(1)} lux`, 9, 'normal', 'left', 0, 2)
  txt(`Mínimo exigido, Art. 10.4 Anexo 3 AM 196 (${resultado.emRequerido.label}): ${resultado.emRequerido.min} lux`, 9, 'normal', 'left', 0, 2)
  txt(`Cumplimiento: ${resultado.cumpleNormativa ? 'CUMPLE' : 'NO CUMPLE'}`, 9, 'bold', 'left', 0, 2)
  // Nota neutra si se supera el máximo del rango del Art. 10.4 (no afecta "Cumplimiento").
  const notaRango = notaSobreRangoArt10(resultado.iluminanciaResultante, resultado.emRequerido)
  if (notaRango) txt(notaRango, 8, 'italic', 'left', 0, 2)
  txt(`Número de luminarias: ${resultado.numeroLuminariasInstaladas}`, 9, 'normal', 'left', 0, 2)
  txt(
    `Espaciado: ${resultado.espaciado.filas} x ${resultado.espaciado.columnas} (espaciado ${resultado.espaciado.espaciadoX.toFixed(2)}m x ${resultado.espaciado.espaciadoY.toFixed(2)}m)`,
    9, 'normal', 'left', 0, 4
  )
  // Uniformidad Uo = Emín/Epromedio (EN 12464-1:2021 por remisión del DE 255 Art. 48) — indicador informativo, no
  // afecta "Cumplimiento". Se recalcula desde lo guardado en inputs (curva
  // de candela de la luminaria, local y reposicionamientos manuales) con la
  // misma función que la pantalla; los cálculos antiguos también lo
  // muestran. Sin datos fotométricos no se imprime.
  const uniformidad = estimateDirectUniformity(inputs.room, resultado.espaciado, inputs.luminaire, inputs.customPositions)
  if (uniformidad) {
    // Misma lógica que la pantalla; lineaPdf/textoPdf evitan "≥" (Helvetica de jsPDF).
    const pres = presentarUniformidadDirecta(uniformidad.uo, resultado.tipoTarea ?? calculo.tipo_tarea_art10)
    txt(`Uniformidad estimada (luz directa): ${pres.lineaPdf}`, 9, 'normal', 'left', 0, 2)
    txt(`${pres.textoPdf}. ${UNIFORMIDAD_DIRECTA_NOTA}`, 8, 'italic', 'left', 0, 2)
    txt(ORIENTACION_FOTOMETRIA_NOTA, 8, 'italic', 'left', 0, 4)
  }
  hline()
  skip(4)

  // d. Luminaria utilizada
  txt('Luminaria utilizada', 11, 'bold', 'left', 0, 4)
  if (inputs.luminaire) {
    txt(`Fabricante: ${inputs.luminaire.manufacturer}`, 9, 'normal', 'left', 0, 2)
    txt(`Código (LUMCAT): ${inputs.luminaire.lumcat}`, 9, 'normal', 'left', 0, 2)
    txt(`Familia: ${inputs.luminaire.family}`, 9, 'normal', 'left', 0, 2)
    txt(`Lúmenes totales: ${inputs.luminaire.total_lumens.toFixed(0)} lm`, 9, 'normal', 'left', 0, 2)
    txt(`Potencia: ${inputs.luminaire.input_wattage} W`, 9, 'normal', 'left', 0, 2)
    // Cálculos guardados antes de 20260928010000 no traen categoria: sin aviso.
    // El distribuidor en Ecuador no va en el PDF (dato informativo de pantalla).
    if (inputs.luminaire.categoria === 'Área clasificada') {
      txt(AVISO_AREA_CLASIFICADA, 8, 'italic', 'left', 0, 2)
    }
  } else {
    txt('Sin datos de luminaria asociados a este cálculo.', 9, 'italic', 'left', 0, 2)
  }
  if (resultado.fuenteFactorUtilizacion === 'estimado') {
    txt(
      'CU estimado — valor orientativo sin verificar contra la ficha técnica del fabricante.',
      8, 'italic', 'left', 0, 4
    )
  }
  hline()
  skip(4)

  // e. Planta del local a escala
  helpers.checkBreak(PLANT_BOX_HEIGHT_MM + 20) // título + caja completa
  txt('Planta del local (a escala)', 11, 'bold', 'left', 0, 6)
  drawPlantOnPdf(helpers, inputs.room, resultado.espaciado, inputs.customPositions)

  // f. Disclaimer
  txt(
    'Cálculo realizado mediante el método de los lúmenes (CITCEA-UPC), conforme lo exige el ' +
    'Art. 10.3 del Anexo 3 - AM 196, bajo las asunciones de luminarias distribuidas uniformemente, ' +
    'local vacío, superficies difusas y de color neutro. El resultado representa una iluminancia ' +
    'promedio — valores puntuales pueden variar. Para diseños que requieran análisis punto por ' +
    'punto, se recomienda asesoría de un profesional en iluminación.',
    8, 'italic', 'left', 0, 4
  )

  const blob = doc.output('blob')
  const filename = `Iluminacion_${calculo.nombre_local.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`
  doc.save(filename)

  return uploadAndRegisterPdf(companyId, 'gestion-tecnica', `${calculo.id}-iluminacion`, blob, filename, {
    category: 'procedimiento',
    title: `Reporte de Iluminación - ${calculo.nombre_local}`,
    description: 'Generado automáticamente desde Metodología de Iluminación (Art. 10 Anexo 3 AM 196)',
    checklist_item_id: 'GT-15',
    uploaded_by: uploadedBy,
  })
}
